import {
  actionChannel,
  call,
  cancel,
  fork,
  join,
  put,
  take,
} from "typed-redux-saga/macro";
import { Action } from "redux";
import {
  GraphQLHttpResponse,
  GraphQLResponseFailureAction,
  GraphQLResponseSuccessAction,
  GraphQLServiceFn,
  GraphQLQuery,
} from "../services/GraphQLTypes";
import {
  ApiServiceFn,
  // ApiServiceHttpResponse,
  ApiServiceResponseSuccessAction,
  ApiServiceResponseFailureAction,
  HeaderType,
} from "../services/ApiServiceTypes";
import { channel, Task } from "redux-saga";

interface ActionTypeObject {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
}

export function* callGraphQLApi(
  actionType: ActionTypeObject,
  serviceFn: GraphQLServiceFn,
  query: GraphQLQuery,
) {
  try {
    const response: GraphQLHttpResponse = yield* call(serviceFn, query);
    if (response.data === undefined && response.errors !== undefined) {
      throw new Error(response.errors[0].message);
    }
    const successAction: GraphQLResponseSuccessAction = {
      payload: {
        variables: query.variables,
        ...response,
      },
      type: actionType.SUCCESS,
    };
    yield* put(successAction);
    return successAction;
  } catch (error: any) {
    const failureAction: GraphQLResponseFailureAction = {
      error: error.message,
      type: actionType.FAILURE,
    };
    yield* put(failureAction);
    return failureAction;
  }
}

export interface ResponsePayloadIds {
  catalogId?: string;
  nodeId?: string;
}

export function* callApi(
  actionType: ActionTypeObject,
  serviceFn: ApiServiceFn,
  payload: any | null,
  endpoint: string,
  headersObj?: HeaderType,
  options?: {
    showGenericError?: boolean;
    meta?: any; // allows the caller to pass extra information that can be used by the subsequent success or failure actions
  },
): Generator<any> {
  try {
    let response = yield* call(serviceFn, payload, endpoint, headersObj);

    if (response !== undefined) {
      if (headersObj && headersObj["x-store-id"] && response) {
        response.storeId = headersObj["x-store-id"];
      }

      if (payload?.catalogId) {
        response.catalogId = payload.catalogId;
      }

      if (payload?.nodeId) {
        response.nodeId = payload.nodeId;
      }

      // Preserve response.next if it exists (for pagination), as it should not be overwritten by request payload
      const responseNext = response?.next;

      const successAction: ApiServiceResponseSuccessAction = {
        payload: {
          ...response,
          ...payload,
          next: responseNext,
        },
        endpoint: endpoint,
        type: actionType.SUCCESS,
        meta: options?.meta,
      };
      yield* put(successAction);
      return successAction;
    } else {
      const failureAction: ApiServiceResponseFailureAction = {
        endpoint: endpoint,
        type: actionType.FAILURE,
        showGenericError: options?.showGenericError,
        meta: options?.meta,
      };
      yield* put(failureAction);
      return failureAction;
    }
  } catch (error: any) {
    const errMessage = error.response?.data?.reason
      ? error.response?.data?.message
      : undefined;
    const failureAction: ApiServiceResponseFailureAction = {
      reason: error.response?.data?.reason,
      message: errMessage,
      endpoint: endpoint,
      type: actionType.FAILURE,
      showGenericError: options?.showGenericError,
      meta: options?.meta,
    };
    yield* put(failureAction);
    return failureAction;
  }
}

export function* delay(ms) {
  yield new Promise((res) => setTimeout(res, ms));
  return;
}

type NotUndefined = {} | null;

const END = "**END**";

export function* createWorkerPool<T extends NotUndefined>(
  concurrency: number,
  workerFn: (workItem: T) => Generator<any>,
) {
  const workChannel = yield* call(channel<T | typeof END>);
  const tasks: Task[] = [];

  function* worker() {
    while (true) {
      const workItem = yield* take(workChannel);
      if (workItem === END) {
        break;
      }
      yield* call(workerFn, workItem);
    }
  }

  for (let i = 0; i < concurrency; i++) {
    const task = yield* fork(worker);
    tasks.push(task);
  }

  function* submit(workItem: T) {
    yield* put(workChannel, workItem);
  }

  function* awaitAll() {
    for (let i = 0; i < concurrency; i++) {
      yield* put(workChannel, END);
    }
    yield* join(tasks);
  }

  return {
    submit,
    awaitAll,
  };
}

export function* watchActionWithPool<A extends Action>(
  actionType: A["type"],
  concurrency: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workerFn: (action: A) => Generator<any, any, any>,
  cancelOnActions: string[] = [],
) {
  function* startPool() {
    const buffered = yield* actionChannel(actionType);
    const workers: Task[] = [];
    for (let i = 0; i < concurrency; i++) {
      const w = yield* fork(function* worker() {
        while (true) {
          const action = (yield* take(buffered)) as A;
          try {
            yield* call(workerFn, action);
          } catch (err) {
            console.error(
              `watchActionWithPool worker for ${String(actionType)} threw:`,
              err,
            );
          }
        }
      });
      workers.push(w);
    }
    return workers;
  }

  let workers = yield* startPool();

  if (cancelOnActions.length === 0) return;

  while (true) {
    yield* take(cancelOnActions);
    // Cancel in-flight workers; any actions still queued in the actionChannel
    // are discarded along with the channel when we recreate the pool below.
    for (const w of workers) {
      yield* cancel(w);
    }
    workers = yield* startPool();
  }
}
